import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AuthService } from 'app/um/services/auth-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { DiagnosisFlagReloadEvent } from 'app/um/lib/events';
import { AddFlaggedDiagnoses } from 'app/um/components/diagnosis-flags/add-flagged-diagnoses';
import { IPopup } from 'app/shared/elements/popup-base';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class FlaggedCodes extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected selectedDiagnosisFlag: UM.AuthFlag | null = null;

    protected diagnosisFlags: ReadonlyArray<UM.AuthFlag> = [];
    protected flaggedDiagnoses: ReadonlyArray<CD.Diagnosis> = [];

    protected sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    private diagnosisFlagReload: Subscription | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $addFlaggedDiagnosesModal!: IPopup;
    protected $form!: IForm;
    protected $flaggedDiagnosisForm!: Element;
    protected $addFlaggedDiagnoses!: AddFlaggedDiagnoses;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly service: AuthService,
        protected readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.diagnosisFlagReload = this.events.subscribe(DiagnosisFlagReloadEvent, (e: DiagnosisFlagReloadEvent) => {
            this.selectedDiagnosisFlag = null;
            this.loadDiagnosisFlags();
        });
        this.loadDiagnosisFlags();
    }

    protected detached(): void {
        if(this.diagnosisFlagReload) this.diagnosisFlagReload.dispose();
    }

    ////****************** Events ***************************************************////
    protected selectedDiagnosisFlagChanged(): void {
        this.flaggedDiagnoses = [];
        this.resultCount = 0;
        this.reload();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        if(this.resultCount > 1) {
            const { property, direction } = event.detail;
            const { sorting } = this;
            if(direction === null) {
                sorting.remove(property);
            } else {
                sorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.loadFlaggedDiagnoses();
        }
    }

    protected async formSuccess(): Promise<void> {
        this.addFlaggedDiagnosesModalClose();
        await Promise.all([this.$addFlaggedDiagnoses.reload(), this.reload()]);
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadFlaggedDiagnoses();
    }

    protected addFlaggedDiagnoses(): void {
        this.$addFlaggedDiagnosesModal.open();
    }

    protected addFlaggedDiagnosesModalClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$addFlaggedDiagnosesModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadDiagnosisFlags(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthFlags({ all: true }, [], 'diagnoses');
            this.diagnosisFlags = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadFlaggedDiagnoses(): Promise<void> {
        if(!this.selectedDiagnosisFlag?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadDiagnosesByAuthFlag(this.selectedDiagnosisFlag.id, options);
            this.flaggedDiagnoses = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async deleteFlaggedDiagnosis(flaggedDiagnosis: CD.Diagnosis): Promise<void> {
        if(!this.selectedDiagnosisFlag) return;

        // Deactivation is all flagged-codes allows, so - always pop confirmation
        const result = await confirmation.showDelete('um:auth.diagnosis.code.flagged.$', 1);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        const updateFlag: UM.AuthFlag = {
            id: this.selectedDiagnosisFlag.id,
            diagnoses: [{ id: flaggedDiagnosis.id }]
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.deleteAuthFlagDetail(updateFlag);
            isSuccessful = response.isSuccessful;
            Promise.all([this.$addFlaggedDiagnoses.reload(), this.reload()]);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('um:auth.diagnosis.code.flagged.$', isSuccessful);
            this.state.endSave();
        }
    }
}
